import moment from 'moment';

/**
 * 출석부 격자 계산. 기간 길이에 따라 열 단위를 정하고(하루/주/달), 회원별로 칸을 채운다.
 *
 * <p>방문은 하루 한 번만 센다. 예약해 놓고 안 온 날은 결석으로 남긴다 — 안 온 날이
 * 빈칸이면 볼 이유가 없는 표가 된다.
 *
 * <p>화면 로직에서 떼어낸 이유는 날짜 격자 수학 120줄이 렌더 코드 한가운데 있어
 * 출결 탭 본체가 읽히지 않았기 때문이다. 순수 함수라 계산만 하고 React 를 모른다.
 * 반환값은 {unit, columns, members, totals} 이고, 기간이 잘못되면 null 이다.
 */
export const buildAttendanceBook = ({
  statRows,
  attendDays,
  members,
  keyword,
  startDate,
  endDate,
  t,
}) => {
  const start = moment(startDate);
  const end = moment(endDate);
  if (!start.isValid() || !end.isValid()) return null;
  const days = end.diff(start, 'days') + 1;
  // 한 주면 하루씩 넓게, 한 달이면 하루씩, 한 분기면 주 단위, 그보다 길면 달 단위로 접는다.
  const unit = days <= 8 ? 'week' : days <= 31 ? 'day' : days <= 120 ? 'weekly' : 'month';

  const columns = [];
  if (unit === 'month') {
    const cursor = start.clone().startOf('month');
    while (cursor.isSameOrBefore(end, 'month')) {
      columns.push({
        key: cursor.format('YYYY-MM'),
        label: cursor.format('M월'),
        sub: cursor.month() === 0 || columns.length === 0 ? cursor.format('YYYY') : '',
        isWeekend: false,
      });
      cursor.add(1, 'month');
    }
  } else if (unit === 'weekly') {
    const cursor = start.clone().startOf('week');
    while (cursor.isSameOrBefore(end, 'day')) {
      columns.push({
        key: cursor.format('YYYY-MM-DD'),
        label: cursor.format('M/D'),
        sub: t('주'),
        isWeekend: false,
      });
      cursor.add(1, 'week');
    }
  } else {
    const cursor = start.clone();
    while (cursor.isSameOrBefore(end, 'day')) {
      columns.push({
        key: cursor.format('YYYY-MM-DD'),
        label: unit === 'week' ? cursor.format('M/D') : cursor.format('D'),
        sub: cursor.format('dd'),
        isWeekend: cursor.day() === 0 || cursor.day() === 6,
      });
      cursor.add(1, 'day');
    }
  }

  const columnKeyOf = date => {
    if (unit === 'month') return String(date).slice(0, 7);
    if (unit === 'weekly') return moment(date).startOf('week').format('YYYY-MM-DD');
    return date;
  };

  const trimmed = keyword.trim();
  const blank = () => ({ cells: new Map(), visited: 0, absent: 0, reserved: 0 });
  /*
   * 교실 명부가 먼저다. 한 번도 안 나온 회원이 표에서 사라지면 출석부가 아니다.
   * 그래서 그룹 소속 회원을 모두 깔고, 그 위에 방문을 얹는다.
   */
  const byMember = new Map();
  members.forEach(member => {
    if (trimmed && !(member.name || '').includes(trimmed)) return;
    byMember.set(member.memberId, { memberId: member.memberId, name: member.name || '-', ...blank() });
  });

  // 날짜별로 한 번 모은 뒤 칸으로 접는다. 같은 날 여러 수업이 있어도 방문은 하루 한 번이다.
  const byDay = new Map();
  const touchDay = (memberId, name, date) => {
    if (!byMember.has(memberId) && trimmed && !(name || '').includes(trimmed)) return null;
    if (!byMember.has(memberId)) {
      // 기간 중 그룹을 떠난 회원도 그날엔 있었으므로 남긴다.
      byMember.set(memberId, { memberId, name: name || '-', ...blank() });
    }
    const dayKey = `${memberId}|${date}`;
    const day = byDay.get(dayKey)
      || { memberId, date, visited: false, absent: false, reserved: false, items: [] };
    byDay.set(dayKey, day);
    return day;
  };

  attendDays.forEach(entry => {
    const member = members.find(row => row.memberId === entry.memberId);
    const day = touchDay(entry.memberId, member?.name, entry.visitDate);
    if (day) day.visited = true;
  });
  statRows.forEach(row => {
    const day = touchDay(row.memberId, row.memberName, row.classDate);
    if (!day) return;
    if (row.status === 'ATTENDED') day.visited = true;      // 출석은 그 자체로 방문이다
    else if (row.status === 'ABSENT') day.absent = true;
    else day.reserved = true;
    day.items.push(row);
  });

  byDay.forEach(day => {
    const member = byMember.get(day.memberId);
    if (!member) return;
    const cellKey = columnKeyOf(day.date);
    const cell = member.cells.get(cellKey)
      || { visited: 0, absent: 0, reserved: 0, items: [] };
    // 하루는 한 상태만 가진다 — 왔으면 방문, 안 왔는데 예약이 있었으면 결석.
    if (day.visited) { cell.visited += 1; member.visited += 1; }
    else if (day.absent) { cell.absent += 1; member.absent += 1; }
    else if (day.reserved) { cell.reserved += 1; member.reserved += 1; }
    cell.items.push(...day.items);
    if (day.visited && day.items.length === 0) cell.freeEntry = true; // 수업 없이 들른 날
    member.cells.set(cellKey, cell);
  });

  const roster = [...byMember.values()].sort((a, b) => a.name.localeCompare(b.name, 'ko'));

  // 맨 아래 합계 — 그 열에 방문한 인원 수
  const totals = new Map();
  columns.forEach(column => {
    let visited = 0;
    roster.forEach(member => {
      if ((member.cells.get(column.key)?.visited ?? 0) > 0) visited += 1;
    });
    totals.set(column.key, visited);
  });

  return { unit, columns, members: roster, totals };
};
